// src/components/tokenPage/valuation/McTvlHistoryChart.tsx

import React, { useMemo, useState } from "react";
import TimeRangePicker from "@/components/common/TimeRangePicker";
import { DataCard } from "@/components/ui";
import { TimeSeriesChart } from "@/components/charts/base";
import { useTokenFundamentals } from "@/contexts/token";
import { sliceByRange } from "@/lib/metrics";

type Range = '90d' | '180d' | '365d' | 'All';

/**
 * McTvlHistoryChart - Shows historical Market Cap / TVL ratio
 * 
 * Uses TokenFundamentalsContext for data (no inline fetching).
 */
const McTvlHistoryChart: React.FC = () => {
  const [range, setRange] = useState<Range>('365d');
  const { mcToTvlHistory, isLoading } = useTokenFundamentals();

  const { dates, values } = useMemo(() => {
    if (mcToTvlHistory.dates.length === 0) {
      return { dates: [], values: [] };
    }
    
    const targetLen = range === '90d' ? 90 : range === '180d' ? 180 : range === '365d' ? 365 : mcToTvlHistory.dates.length;
    return sliceByRange(mcToTvlHistory.dates, mcToTvlHistory.values, targetLen);
  }, [mcToTvlHistory, range]);

  // Hide if no data available
  if (!isLoading && (!values.length || values.every(v => !Number.isFinite(v)))) {
    return null;
  }

  return (
    <DataCard
      title="Market Cap / TVL"
      subtitle="Historical ratio"
      icon="trending-up"
      iconColor="text-blue-400"
      isLoading={isLoading}
      loadingVariant="chart"
      actions={
        <TimeRangePicker options={['90d','180d','365d','All'] as const} value={range} onChange={setRange} theme="blue" />
      }
    >
      <TimeSeriesChart
        dates={dates}
        series={[{ name: 'Market Cap / TVL', values, color: '#60A5FA', areaStyle: true }]}
        height={220}
        yAxisFormat="ratio"
        showMeanBands
        showLegend={false}
      />
    </DataCard>
  );
};

export default McTvlHistoryChart;
